<script lang="ts">
  import { onMount, untrack, type Snippet } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import {
    mdiCheckCircleOutline,
    mdiArrowLeft,
    mdiArrowRight,
    mdiUndo,
    mdiOpenInNew,
  } from "@mdi/js"
  import type {
    GuideCourse,
    GuideEntryTarget,
  } from "../../../../../../../../api/kehrblatt/learning-guide"
  import { spaLink, spaNavigate } from "../../../../../../../../_shared/_logic/navigation"
  import { location } from "../../../../../../../../_shared/_logic/store"
  import { learningLessonPath } from "../../../../config"
  import {
    getNextLearningLesson,
    getLearningLessonHref,
    getLearningLessonNumber,
    getLearningOverviewHref,
  } from "../../../../../../_shared/_logic/learning-path"
  import { LessonController } from "./_logic/lessonController.svelte"
  import { readStageLocation, stageLocation } from "./_logic/stageLocation"
  import GuideFigure from "./_modules/guide-figure"
  import StageContent from "./_modules/stage-content"
  import LearningDisclosure from "../../_shared/_modules/learning-disclosure"

  const {
    lessonKey,
    course,
    basePath,
    saving,
    versionConflict,
    onSetCompleted,
    onReloadCourse,
    entryDestinations,
    entryDestinationsLoading = false,
    renderChapters,
  } = $props<{
    lessonKey: string
    course: GuideCourse
    basePath: string
    saving: boolean
    versionConflict: boolean
    onSetCompleted: (
      lessonKey: string,
      completed: boolean,
      contentVersion: number,
    ) => Promise<boolean>
    onReloadCourse: () => Promise<void>
    entryDestinations: Partial<Record<GuideEntryTarget, string>>
    entryDestinationsLoading?: boolean
    renderChapters: Snippet<[() => void]>
  }>()
  const controller = untrack(() => new LessonController(lessonKey))
  const chapter = $derived(controller.lesson)
  const completed = $derived(
    course.lessons.find((lesson) => lesson.key === lessonKey)?.completed ?? false,
  )
  const ordered = $derived([...course.lessons].sort((a, b) => a.position - b.position))
  const next = $derived(getNextLearningLesson(course.lessons, lessonKey, $location.search))
  const overviewHref = $derived(getLearningOverviewHref(basePath, $location.search))
  const nextHref = $derived(
    next ? getLearningLessonHref(basePath, next, $location.search) : overviewHref,
  )
  const prerequisites = $derived(
    ordered.filter((lesson) => chapter?.lesson.prerequisite_lesson_keys.includes(lesson.key)),
  )
  const related = $derived(
    ordered.filter((lesson) => chapter?.related_lesson_keys.includes(lesson.key)),
  )
  const position = $derived(
    readStageLocation(chapter?.stages ?? [], chapter?.choices ?? [], $location.search),
  )
  const activeStage = $derived(chapter?.stages[position.stageIndex])
  const lastStage = $derived(!!chapter && position.stageIndex === chapter.stages.length - 1)
  const chapterPath = $derived(chapter ? learningLessonPath(basePath, chapter.lesson) : "")
  const entryPoint = $derived(
    activeStage?.entry_point ?? (position.stageIndex === 0 ? chapter?.entry_point : null),
  )
  const entryHref = $derived(entryPoint ? entryDestinations[entryPoint.target] : null)
  let contentsOpen = $state(false)
  let stageHeading: HTMLHeadingElement | undefined = $state()
  let lastFocusedStage: string | null = null
  let disposed = false

  onMount(() => {
    void controller.load()

    return () => {
      disposed = true
      controller.dispose()
    }
  })

  // Reading-position changes move keyboard focus, never personal progress.
  $effect(() => {
    const key = activeStage?.key

    if (!key) return

    untrack(() => {
      if (lastFocusedStage !== null && lastFocusedStage !== key) {
        stageHeading?.focus({ preventScroll: true })
        stageHeading?.scrollIntoView({ block: "start" })
      }

      lastFocusedStage = key
    })
  })

  function goToStage(index: number) {
    const stage = chapter?.stages[index]

    if (stage) {
      contentsOpen = false
      spaNavigate(stageLocation(chapterPath, $location.search, stage.key), { noSearchParam: true })
      stageHeading?.focus({ preventScroll: true })
    }
  }

  function choosePath(key: string, option: string) {
    if (!activeStage) return

    spaNavigate(stageLocation(chapterPath, $location.search, activeStage.key, { key, option }), {
      noSearchParam: true,
    })
  }

  async function finishLesson() {
    if (!chapter || saving || versionConflict) return

    const saved = await onSetCompleted(lessonKey, true, chapter.lesson.content_version)

    if (saved && !disposed) spaNavigate(nextHref, { noSearchParam: true })
  }

  function reload() {
    void onReloadCourse()
    void controller.load()
  }
</script>

{#if controller.loading}
  <p role="status">Anleitung wird geladen …</p>
{:else if controller.loadError || !chapter || !activeStage}
  <div role="alert">
    <p>Die Anleitung konnte nicht geladen werden.</p>
    <CTA type="secondaryButton" content="Erneut laden" darkMode={false} onClick={reload} />
  </div>
{:else}
  <article class="lesson" aria-labelledby="guide-lesson-title">
    <header class="lesson-heading">
      <div class="lesson-meta">
        <p class="lesson-position"
          >Kapitel {getLearningLessonNumber(lessonKey) ?? chapter.lesson.position} von {course.total_count}<span
            class="meta-divider"
            aria-hidden="true">·</span
          ><span>Schritt {position.stageIndex + 1} von {chapter.stages.length}</span></p
        >
        {#if completed}<p class="lesson-done"
            ><Icon path={mdiCheckCircleOutline} size="1.1rem" />Erledigt</p
          >{/if}
      </div>
      <h1 id="guide-lesson-title">{chapter.lesson.title}</h1>
      {#if position.stageIndex === 0 && chapter.introduction}<p>{chapter.introduction}</p>{/if}
      {#if position.stageIndex === 0 && lessonKey === "navigation"}
        <p class="setup-hint"
          >Ihr Konto ist noch nicht eingerichtet? <a
            use:spaLink
            href={`${basePath}?lernweg=einrichtung`}>Hier starten.</a
          ></p
        >
      {/if}
    </header>
    <div class="lesson-contents">
      <LearningDisclosure
        title={contentsOpen ? "Inhalt ausblenden" : "Inhalt anzeigen"}
        bind:open={contentsOpen}
      >
      {#if contentsOpen}
        <div id="guide-contents" class="contents-list">
          {#if prerequisites.length}
            <section class="prerequisites" aria-label="Vorher hilfreich">
              <h2>Vorher hilfreich</h2>
              {#each prerequisites as lesson (lesson.key)}
                <a
                    class="disclosure-link"
                    use:spaLink
                    href={getLearningLessonHref(basePath, lesson, $location.search)}
                    >{lesson.title}</a
                  >
              {/each}
            </section>
          {/if}
          <nav class="chapter-steps" aria-label="Schritte dieser Anleitung">
            <h2>In dieser Anleitung</h2>
            <ol>
              {#each chapter.stages as stage, index (stage.key)}
                <li
                    ><button
                      type="button"
                      aria-current={index === position.stageIndex ? "step" : undefined}
                      aria-controls="guide-active-stage"
                      aria-label={`${index + 1}. ${stage.title}`}
                      onclick={() => goToStage(index)}
                    >
                  <span>{index + 1}.</span><span>{stage.title}</span>
                </button></li
                  >
              {/each}
            </ol>
          </nav>
          <div class="all-chapters">{@render renderChapters(() => (contentsOpen = false))}</div>
        </div>
      {/if}
      </LearningDisclosure>
    </div>
    <section id="guide-active-stage" class="active-stage" aria-labelledby="guide-stage-title">
      <header class="stage-heading">
        <h2 id="guide-stage-title" tabindex="-1" bind:this={stageHeading}>{activeStage.title}</h2>
        {#if activeStage.summary}<p>{activeStage.summary}</p>{/if}
      </header>
      {#if entryPoint}
        <div class="entry-point">
          <p>{entryPoint.instruction}</p>
          {#if entryDestinationsLoading}
            <p role="status">Einstieg wird geprüft …</p>
          {:else if entryHref}
            <a class="entry-link" href={entryHref} target="_blank" rel="noopener noreferrer">
              <span>{entryPoint.label}</span><Icon path={mdiOpenInNew} size="1.1rem" />
              <small>Öffnet einen neuen Tab</small>
            </a>
          {:else}
            <p class="entry-unavailable"
              >Dieser Bereich ist für Ihren Zugang nicht verfügbar. Fragen Sie die Person, die Ihren Zugang verwaltet. Die Anleitung können Sie trotzdem lesen.</p
            >
          {/if}
        </div>
      {:else if position.stageIndex === 0 && chapter.entry_path.length}
        <div class="entry-path">
          <strong>Hier beginnen:</strong>
          <ol
            >{#each chapter.entry_path as entry, index (index)}<li>{entry}</li>{/each}</ol
          >
        </div>
      {/if}
      {#key activeStage.key}
        <StageContent
          blocks={activeStage.blocks}
          choices={chapter.choices}
          selected={position.selected}
          onChoose={choosePath}
        >
          {#snippet renderImage(image)}<GuideFigure {image} />{/snippet}
        </StageContent>
      {/key}
      {#if activeStage.success_text}
        <section class="stage-result">
          <h3>Darauf kommt es an</h3><p>{activeStage.success_text}</p>
        </section>
      {/if}
    </section>
    <footer class="lesson-footer">
      {#if versionConflict}
        <CTA
          type="secondaryButton"
          content="Aktuelle Anleitung laden"
          darkMode={false}
          onClick={reload}
        />
      {/if}
      {#if lastStage}
        <div class="lesson-completion">
          <h2>Anleitung abgeschlossen?</h2>
          <p
            >Sie bestätigen damit nur, dass Sie die Anleitung durchgegangen sind. In Ihrem Betrieb wird dadurch nichts geändert oder versendet.</p
          >
          <CTA
            type={completed ? "secondaryButton" : "primaryButton"}
            content={saving
              ? "Wird gespeichert …"
              : completed
                ? "Wieder als offen markieren"
                : "Als erledigt markieren und weiter"}
            disabled={saving || versionConflict}
            darkMode={false}
            onClick={() =>
              completed
                ? void onSetCompleted(lessonKey, false, chapter.lesson.content_version)
                : void finishLesson()}
          >
            {#snippet iconSnippet()}<Icon
                path={completed ? mdiUndo : mdiCheckCircleOutline}
              />{/snippet}
          </CTA>
        </div>
      {/if}
      <nav class="stage-pagination" aria-label="Weiterlesen">
        {#if position.stageIndex > 0}
          <CTA
            type="secondaryButton"
            content="Zurück"
            darkMode={false}
            onClick={() => goToStage(position.stageIndex - 1)}
          >
            {#snippet iconSnippet()}<Icon path={mdiArrowLeft} />{/snippet}
          </CTA>
        {:else}
          <a use:spaLink href={overviewHref}
            ><Icon path={mdiArrowLeft} size="1.1rem" /> Zum Lernpfad</a
          >
        {/if}
        {#if !lastStage}
          <CTA
            type="primaryButton"
            content={`Weiter: ${chapter.stages[position.stageIndex + 1].title}`}
            darkMode={false}
            onClick={() => goToStage(position.stageIndex + 1)}
          >
            {#snippet iconSnippet()}<Icon path={mdiArrowRight} />{/snippet}
          </CTA>
        {:else}
          <a use:spaLink href={nextHref}>
            {next ? `Ohne Abschluss weiter: ${next.short_label}` : "Zurück zum Lernpfad"}
            <Icon path={mdiArrowRight} size="1.1rem" />
          </a>
        {/if}
      </nav>
      {#if lastStage && related.length}
        <LearningDisclosure title="Passende Anleitungen">
          {#each related as lesson (lesson.key)}<a
              class="disclosure-link"
              use:spaLink
              href={getLearningLessonHref(basePath, lesson, $location.search)}>{lesson.title}</a
            >{/each}
        </LearningDisclosure>
      {/if}
    </footer>
  </article>
{/if}

<style>
  .entry-point {
    border-left: 3px solid #176288;
    padding: 0.35rem 0 0.35rem 1rem;
    display: grid;
    gap: 0.65rem;
    margin-bottom: 1.5rem;
  }
  .entry-link {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.45rem;
    width: fit-content;
    max-width: 100%;
    font-weight: 600;
    line-height: 1.5;
    color: #176288;
  }
  .entry-link small {
    font-size: 14px;
    font-weight: 400;
  }
  .entry-unavailable {
    font-size: 16px;
  }
  .entry-link:focus-visible,
  .setup-hint a:focus-visible {
    outline: 2px solid #176288;
    outline-offset: 3px;
  }
  .setup-hint {
    font-size: 16px;
  }
  .setup-hint a {
    color: #176288;
    text-decoration: underline;
  }
  .lesson {
    min-width: 0;
    color: var(--text-invers-100);
    width: 100%;
    font-size: 18px;
  }
  h1,
  h2,
  h3,
  p {
    margin: 0;
    color: inherit;
  }
  h1 {
    font-size: 28px;
    line-height: 1.3;
    overflow-wrap: anywhere;
  }
  h2 {
    font-size: 22px;
    line-height: 1.4;
  }
  h3 {
    font-size: 19px;
    line-height: 1.4;
  }
  p {
    font-size: 18px;
    line-height: 1.6;
    max-width: 72ch;
    white-space: pre-line;
    overflow-wrap: anywhere;
  }
  .lesson-heading {
    display: grid;
    gap: 0.7rem;
    margin-bottom: 1rem;
  }
  .lesson-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem 1.25rem;
  }
  .meta-divider {
    margin-inline: 0.65rem;
  }
  .lesson-position {
    display: flex;
    flex-wrap: wrap;
    font-size: 15px;
    color: #526974;
  }
  .lesson-done {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    color: #21683e;
    font-size: 15px;
  }
  a {
    color: inherit;
    text-underline-offset: 0.2em;
    overflow-wrap: anywhere;
  }
  .disclosure-link {
    display: block;
    margin-top: 0.6rem;
  }
  .lesson-contents {
    margin-bottom: 1.5rem;
    max-width: 72ch;
  }
  .contents-list {
    max-width: 52rem;
    padding-block: 0.5rem 1rem;
  }
  .chapter-steps,
  .prerequisites,
  .all-chapters {
    margin-bottom: 1.25rem;
  }
  .chapter-steps h2,
  .prerequisites h2 {
    font-size: 18px;
  }
  .chapter-steps ol {
    list-style: none;
    padding: 0;
    margin: 0.5rem 0 0;
  }
  .chapter-steps li {
    min-width: 0;
  }
  .chapter-steps button {
    display: flex;
    align-items: baseline;
    gap: 0.6rem;
    width: 100%;
    padding: 0.65rem 0.5rem;
    font: inherit;
    font-size: 16px;
    line-height: 1.4;
    text-align: left;
    color: inherit;
    background: transparent;
    border: 0;
    cursor: pointer;
  }
  .chapter-steps button[aria-current] {
    background: #f7eeee;
    color: #7c2024;
    font-weight: 700;
  }
  .chapter-steps button:focus-visible,
  a:focus-visible {
    outline: 2px solid #176288;
    outline-offset: 2px;
  }
  .active-stage {
    min-width: 0;
  }
  .stage-heading {
    display: grid;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
  }
  #guide-stage-title {
    scroll-margin-top: 7rem;
  }
  #guide-stage-title:focus-visible {
    outline: 2px solid #176288;
    outline-offset: 5px;
  }
  .entry-path {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.5rem 1rem;
    margin-bottom: 1.5rem;
    padding-left: 0.8rem;
    border-left: 3px solid #7c2024;
    font-size: 16px;
  }
  .entry-path ol {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1.75rem;
    margin: 0;
    padding-left: 1.1rem;
    line-height: 1.5;
  }
  .stage-result {
    border-left: 3px solid #21683e;
    padding: 0.65rem 1rem;
    display: grid;
    gap: 0.4rem;
    margin-top: 1.75rem;
  }
  .lesson-footer {
    display: grid;
    min-width: 0;
    gap: 1.5rem;
    border-top: 1px solid #d8dfe2;
    padding-top: 1.5rem;
    margin-top: 1.75rem;
  }
  .lesson-completion {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    gap: 0.75rem;
  }
  .stage-pagination {
    display: flex;
    min-width: 0;
    max-width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
  }
  .stage-pagination > :global(*) {
    min-width: 0;
    max-width: 100%;
  }
  .stage-pagination a {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    max-width: 100%;
    font-size: 17px;
  }
  .lesson :global(button) {
    min-width: 0;
    max-width: 100%;
    white-space: normal;
    overflow-wrap: anywhere;
  }
  .lesson :global(button p) {
    min-width: 0;
    white-space: normal;
    overflow-wrap: anywhere;
  }
  @media (max-width: 600px) {
    .stage-pagination {
      align-items: stretch;
      flex-direction: column;
    }
    .lesson-position {
      gap: 0.2rem 0;
    }
  }
</style>
